import useAudioData from './useAudioData';
import styles from './Audio.module.css';
import { MediaBadge } from '@round/whitelabel-ui';
import { SkeletonizedValue } from '@round/ui-kit';
import { buildInstagramAudioUrl, buildInstagramUserUrl } from 'helpers';
import { AudioPlayerProvider } from 'Modules/AudioPlayer/AudioPlayerContext';
import AudioPlaybackThumbnail from 'Modules/AudioPlayer/AudioPlaybackThumbnail/AudioPlaybackThumbnail';

type Props = {
    audioId: number;
};

const Audio = ({ audioId }: Props) => {
    const {
        query: { data, isFetching, isError, error },
        helpers: { isExpectedErrorResponse },
    } = useAudioData(audioId);

    const audioPlatformUrl = data?.instagram_id ? buildInstagramAudioUrl(data?.instagram_id) : undefined;
    const userPlatformUrl = data?.author_name ? buildInstagramUserUrl(data.author_name) : undefined;

    const errorTitle = !isExpectedErrorResponse(error)
        ? 'Something went wrong'
        : error.response.status === 404
          ? 'Audio not found'
          : 'Access denied';

    const errorMessage = !isExpectedErrorResponse(error)
        ? 'An unexpected error occurred. Please try again later'
        : error.response.status === 404
          ? "Sorry, we couldn't find any data for this audio"
          : "Sorry, you don't have permission to view this audio";

    return (
        <AudioPlayerProvider>
            <div className={styles.container}>
                <AudioPlaybackThumbnail
                    skeletonCircle={false}
                    loading={isFetching}
                    className={styles.image}
                    imageUrl={data?.thumbnail_url ?? undefined}
                    alt={`${data?.title ?? 'audio'} thumbnail`}
                    audioUrl={data?.audio_file_url ?? undefined}
                />
                <div className={styles.info}>
                    {!isError ? (
                        <>
                            <a
                                className={styles.title}
                                href={audioPlatformUrl}
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                <SkeletonizedValue isInitialized={!isFetching}>
                                    {data?.title ?? data?.instagram_id ?? '-'}
                                </SkeletonizedValue>
                            </a>
                            <a
                                className={styles.subTitle}
                                href={userPlatformUrl}
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                <SkeletonizedValue isInitialized={!isFetching}>
                                    {data?.author_name ?? '-'}
                                </SkeletonizedValue>
                            </a>
                        </>
                    ) : (
                        <>
                            <p className={styles.title}>{errorTitle}</p>
                            <p className={styles.subTitle}>{errorMessage}</p>
                        </>
                    )}

                    <div className={styles.badges}>
                        <MediaBadge type="audio" />
                        <MediaBadge type="instagram" />
                    </div>
                </div>
            </div>
        </AudioPlayerProvider>
    );
};

export default Audio;
